import {makeAutoObservable} from 'mobx';
import LibraryRepository, {
  EditChunkOption,
} from '../../../data/repositories/LibraryRepository';
import LoadingModule from '../../../modules/LoadingModule/LoadingModule';
import {toast} from 'react-toastify';
import {getErrorFromCode} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {GeneratedVideoChunk} from '../../../data/models/LibraryFile/GeneratedVideoChunk';
import {VideoEditorText} from '../VideoEditorText';

export enum VideoEditorState {
  Idle = 0,
  // the codes below match enum LibraryMediaFileStatus from the backend
  MutingAudio = 20,
  Regenerating = 21,
  ChangingMedia = 22,
  ReplacingWithLibraryVideo = 23,
  ReplacingWithLibraryImage = 24,
  ChangingNarration = 25,
  ReplacingAIMedia = 26,
  ChangingBackgroundAudio = 27,
  DisablingBackgroundAudio = 28,
  UpdatingArtifacts = 29,
}

class VideoEditorStore {
  private libraryRepository = new LibraryRepository();
  private loadingModule = new LoadingModule();
  public editChunkErrorCode = '';
  public state: VideoEditorState = VideoEditorState.Idle;
  public isEditingTemporaryDisabled = false;
  public hasBackgroundAudio = false;
  public backgroundAudioName?: string;
  public hasUnsavedChanges = false;

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public async getVideoChunks(organizationId: string, mediaFileId: string) {
    this.loadingModule.startLoading();
    const result =
      await this.libraryRepository.getVideoChunksForGeneratedContent(
        organizationId,
        mediaFileId,
      );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      const {
        chunks,
        isEditingTemporaryDisabled,
        hasBackgroundAudio,
        backgroundAudioName,
        hasUnsavedChanges,
      } = result.data;
      this.isEditingTemporaryDisabled = isEditingTemporaryDisabled;
      this.hasBackgroundAudio = hasBackgroundAudio ?? false;
      this.backgroundAudioName = backgroundAudioName;
      this.hasUnsavedChanges =
        !!hasUnsavedChanges || chunks.some(x => x.isDraft);
      const uniqueStatuses = Array.from(new Set(chunks.map(f => f.fileStatus)));
      if (uniqueStatuses.find(s => s == VideoEditorState.Regenerating)) {
        this.state = VideoEditorState.Regenerating;
      } else if (
        uniqueStatuses.find(s => s == VideoEditorState.ReplacingAIMedia)
      ) {
        this.state = VideoEditorState.ReplacingAIMedia;
      } else if (
        uniqueStatuses.find(s => s == VideoEditorState.ChangingNarration)
      ) {
        this.state = VideoEditorState.ChangingNarration;
      } else if (
        uniqueStatuses.find(
          s =>
            s == VideoEditorState.ReplacingWithLibraryVideo ||
            s == VideoEditorState.ReplacingWithLibraryImage,
        )
      ) {
        this.state = VideoEditorState.ReplacingWithLibraryVideo;
      } else if (
        uniqueStatuses.find(s => s == VideoEditorState.ChangingMedia)
      ) {
        this.state = VideoEditorState.ChangingMedia;
      } else if (uniqueStatuses.find(s => s == VideoEditorState.MutingAudio)) {
        this.state = VideoEditorState.MutingAudio;
      } else if (
        uniqueStatuses.find(s => s == VideoEditorState.DisablingBackgroundAudio)
      ) {
        this.state = VideoEditorState.DisablingBackgroundAudio;
      } else if (
        uniqueStatuses.find(s => s == VideoEditorState.UpdatingArtifacts)
      ) {
        this.state = VideoEditorState.UpdatingArtifacts;
      } else if (uniqueStatuses.every(x => x === 0)) {
        this.state = VideoEditorState.Idle;
      }
      return chunks;
    } else if (result.errors && result.errors.length) {
      throw new Error(getErrorFromCode(result.errors[0]));
    }
    return [];
  }

  public getMediaFile = async (organizationId: string, mediaFileId: string) => {
    const result = await this.libraryRepository.getByContentId(
      organizationId,
      mediaFileId,
    );
    if (result.success && result.data) {
      return result.data;
    }
  };

  public async updateVideoChunksForGeneratedContent(
    organizationId: string,
    mediaFileId: string,
    chunks: GeneratedVideoChunk[],
  ) {
    this.loadingModule.startLoading();
    const result =
      await this.libraryRepository.updateVideoChunksForGeneratedContent(
        organizationId,
        mediaFileId,
        chunks,
      );
    this.loadingModule.endLoading();
    if (result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    }
    return result.success;
  }

  public async compileVideoChunks(organizationId: string, mediaFileId: string) {
    this.loadingModule.startLoading();
    const result = await this.libraryRepository.compileVideoFile(
      organizationId,
      mediaFileId,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      toast.error(getErrorFromCode(result.errors[0]), toastConfig);
    } else if (result.success) {
      toast.success(VideoEditorText.publishSuccess, toastConfig);
    }
    return result.success;
  }

  public async EditChunk(
    organizationId: string,
    mediaFileId: string,
    option: EditChunkOption,
    chunkId: string,
    additionalParameters: Record<string, any> = {},
  ) {
    if (option === EditChunkOption.ChangeFromLibrary) {
      this.state = VideoEditorState.Idle;
      return true;
    }

    this.loadingModule.startLoading();
    switch (option) {
      case EditChunkOption.Regenerate:
        this.state = VideoEditorState.Regenerating;
        break;
      case EditChunkOption.MuteAudio:
        this.state = VideoEditorState.MutingAudio;
        break;
      case EditChunkOption.DisableBackgroundAudio:
        this.state = VideoEditorState.DisablingBackgroundAudio;
        break;
      case EditChunkOption.Delete:
        this.state = VideoEditorState.ChangingMedia;
        break;
      case EditChunkOption.Reorder:
      case EditChunkOption.ChangeMedia:
      default:
        this.state = VideoEditorState.Idle;
        break;
    }
    const result = await this.libraryRepository.editChunk(
      organizationId,
      mediaFileId,
      option,
      chunkId,
      additionalParameters,
    );
    this.loadingModule.endLoading();
    if (result.success) {
      //this.editVideoErrorMessage = editVideoSourceText.regenerateSuccess;
    } else if (result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }

  public async saveChanges(organizationId: string, mediaFileId: string) {
    this.loadingModule.startLoading();
    const result = await this.libraryRepository.saveEdit(
      organizationId,
      mediaFileId,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
    }
    return result.success;
  }

  public async revertChanges(organizationId: string, mediaFileId: string) {
    this.loadingModule.startLoading();
    await this.libraryRepository.cancelEdit(organizationId, mediaFileId);
    this.loadingModule.endLoading();
  }

  public async updateNarration(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    text: string,
  ) {
    this.loadingModule.startLoading();
    this.state = VideoEditorState.ChangingNarration;
    const result = await this.libraryRepository.updateChunkNarration(
      organizationId,
      mediaFileId,
      chunkId,
      text,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }

  public async replaceAIMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    replacements: {artifactUri: string; libraryMediaFileId: string}[],
  ) {
    this.loadingModule.startLoading();
    this.state = VideoEditorState.ReplacingAIMedia;
    const result = await this.libraryRepository.replaceAIMedia(
      organizationId,
      mediaFileId,
      chunkId,
      replacements,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }

  public async updateChunkArtifacts(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    artifacts: {
      artifactId: string | null;
      libraryMediaFileId: string | null;
      storyBlocksVideoId: number | null;
      order: number;
      startTime: number;
      duration: number;
    }[],
  ) {
    this.loadingModule.startLoading();
    this.state = VideoEditorState.UpdatingArtifacts;
    const result = await this.libraryRepository.updateChunkArtifacts(
      organizationId,
      mediaFileId,
      chunkId,
      artifacts,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }

  public async replaceChunkWithLibraryMedia(
    organizationId: string,
    mediaFileId: string,
    chunkId: string,
    libraryMediaId: string,
    isImage: boolean,
  ) {
    this.loadingModule.startLoading();
    this.state = isImage
      ? VideoEditorState.ReplacingWithLibraryImage
      : VideoEditorState.ReplacingWithLibraryVideo;
    const result = await this.libraryRepository.changeChunkToLibraryMedia(
      organizationId,
      mediaFileId,
      chunkId,
      libraryMediaId,
      isImage,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      this.editChunkErrorCode = result.errors[0];
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }

  public async addChunk(
    organizationId: string,
    mediaFileId: string,
    payload: {
      libraryMediaFileId?: string;
      storyBlocksVideoId?: number;
      start: number;
      stop: number;
      mute: boolean;
    },
  ): Promise<boolean | 'timeout'> {
    this.loadingModule.startLoading();
    this.state = VideoEditorState.ChangingMedia;
    const result = await this.libraryRepository.addChunk(
      organizationId,
      mediaFileId,
      payload,
    );
    this.loadingModule.endLoading();
    if (!result.success && result.errors && result.errors.length) {
      const errorCode = result.errors[0];
      if (errorCode === 'timeout') {
        toast.info(VideoEditorText.addMediaPending, toastConfig);
        this.state = VideoEditorState.Idle;
        return 'timeout';
      } else {
        this.editChunkErrorCode = errorCode;
      }
      this.state = VideoEditorState.Idle;
    }
    return result.success;
  }
}

export default VideoEditorStore;
